import { useState } from 'react';

import BpkBottomSheet from '@skyscanner-internal/backpack-web/bpk-component-bottom-sheet';
import BpkBreakpoint, {
  BREAKPOINTS,
} from '@skyscanner-internal/backpack-web/bpk-component-breakpoint';
import {
  BpkBreakpoint as BpkLayoutBreakpoint,
  BpkFlex,
  BpkSpacing,
  BpkStack,
  BpkVessel,
} from '@skyscanner-internal/backpack-web/bpk-component-layout';
import BpkLink from '@skyscanner-internal/backpack-web/bpk-component-link';
import BpkText, {
  TEXT_STYLES,
} from '@skyscanner-internal/backpack-web/bpk-component-text';
import BpkTooltip from '@skyscanner-internal/backpack-web/bpk-component-tooltip';

import type { PriceBandSubtitleData } from '@skyscanner-internal/landing-pages-shared-types/types-shared/DealsEvolved';

import STYLES from './DealsEvolved.module.scss';

const MobilePriceBandTooltip = ({
  tooltipLink,
  tooltipText,
}: {
  tooltipLink: string;
  tooltipText: string;
}) => {
  const [sheetOpen, setSheetOpen] = useState(false);
  return (
    <>
      <BpkLink
        as="button"
        type="button"
        className={STYLES.DealsEvolved__priceBandSubtitle__tooltipLink}
        aria-describedby="price-band-mobile-tooltip"
        onClick={() => setSheetOpen(true)}
      >
        <BpkText tagName="span" textStyle={TEXT_STYLES.caption}>
          {tooltipLink}
        </BpkText>
      </BpkLink>
      <span id="price-band-mobile-tooltip" role="tooltip" hidden>
        {tooltipText}
      </span>
      <BpkBottomSheet
        id="price-band-bottom-sheet"
        isOpen={sheetOpen}
        onClose={() => setSheetOpen(false)}
        title={tooltipLink}
        ariaLabel={tooltipLink}
        closeOnScrimClick
      >
        <BpkText tagName="p" textStyle={TEXT_STYLES.footnote}>
          {tooltipText}
        </BpkText>
      </BpkBottomSheet>
    </>
  );
};

const PriceBandSubtitle = ({
  priceBandSubtitle,
}: {
  priceBandSubtitle: PriceBandSubtitleData;
}) => (
  <BpkVessel className={STYLES.DealsEvolved__priceBandSubtitle}>
    <BpkStack
      direction={{
        base: 'column',
        [BpkLayoutBreakpoint.SmallTablet]: 'row',
      }}
      justify="space-between"
      align="flex-start"
      gap={BpkSpacing.Base}
      padding={BpkSpacing.Base}
    >
      <BpkFlex grow={1} shrink={1} basis="0%" direction="column">
        <BpkText
          textStyle={TEXT_STYLES.footnote}
          className={STYLES.DealsEvolved__priceBandSubtitle__heading}
        >
          {priceBandSubtitle.heading}
        </BpkText>
        <BpkText
          textStyle={TEXT_STYLES.footnote}
          className={STYLES.DealsEvolved__priceBandSubtitle__text}
        >
          {priceBandSubtitle.subtitle}
        </BpkText>
      </BpkFlex>
      <BpkFlex align="flex-start">
        <BpkBreakpoint query={BREAKPOINTS.MOBILE}>
          {(isMobile: boolean) =>
            isMobile ? (
              <MobilePriceBandTooltip
                tooltipLink={priceBandSubtitle.tooltipLink}
                tooltipText={priceBandSubtitle.tooltipText}
              />
            ) : (
              <BpkTooltip
                ariaLabel={priceBandSubtitle.tooltipText}
                id="price-band-tooltip"
                target={
                  <BpkLink
                    as="button"
                    type="button"
                    className={
                      STYLES.DealsEvolved__priceBandSubtitle__tooltipLink
                    }
                  >
                    <BpkText tagName="span" textStyle={TEXT_STYLES.caption}>
                      {priceBandSubtitle.tooltipLink}
                    </BpkText>
                  </BpkLink>
                }
              >
                <BpkText
                  tagName="p"
                  textStyle={TEXT_STYLES.footnote}
                  className={
                    STYLES.DealsEvolved__priceBandSubtitle__tooltipContent
                  }
                >
                  {priceBandSubtitle.tooltipText}
                </BpkText>
              </BpkTooltip>
            )
          }
        </BpkBreakpoint>
      </BpkFlex>
    </BpkStack>
  </BpkVessel>
);

export default PriceBandSubtitle;
